import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

export const createQuotesShowcaseVideoSkill: Skill = {
    name: SKILL_NAMES.createQuotesShowcaseVideo,
    description:
        'Quotes shown one per scene with the speaker name, title, and photo, set to music with no voiceover. Use for testimonials, customer reviews, and pull-quote highlights.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'Quotes Showcase',
        description: 'Focuses on quotes paired with speaker photos',
        preview: 'quotes-showcase',
        frames: [
            'quotes-showcase-1',
            'quotes-showcase-2',
            'quotes-showcase-3',
            'quotes-showcase-4',
            'quotes-showcase-5',
        ],
    },
    instructions: `# Create Quotes Showcase Video
For a video built entirely from quotes: one quote, or several delivered
back-to-back, each shown as its own Quote-category scene carrying the quote
text, the speaker's name, their title, and a photo of them cropped to focus
on their face. Music plays throughout; there is no voiceover.

## Setup
Use \`${SKILL_NAMES.gatherRequirements}\` to get necessary info, skipping anything already
provided. Use that skill's general suggestions, but also specifically make sure we get:
- The source content — a page or document to pull quotes from, or the exact
  quote text with each speaker's name and title. Quotes are used verbatim
  from whichever source they come from, never paraphrased or summarized
- Photos of the people quoted — only ask if a photo for a given speaker
  wasn't already supplied or found in the source material
- How many quotes / ideal duration — this matters most when the source
  material turns up more quotes than the target length can hold

Don't ask about voiceover — this format is music-only.

Show users options of templates with a Quote scene design that fits the tone of the
content. Search if none of the available ones seem an obvious fit.

## Narration and music
- **No voiceover, ever.** This format is music-only — there is no narration
  track at all.
- **Music must actually end up in the video.** Search \`${TOOL_NAMES.searchMusic}\` for a
  track matching the tone of the quotes themselves (reflective and warm for
  personal/inspirational quotes, polished and confident for professional
  testimonials — read the content to judge which), then apply the chosen
  track to the video (don't stop at searching) and set its volume to sit
  comfortably under the reading experience. Before moving on, confirm with
  \`${TOOL_NAMES.getVideoState}\` that a music track is actually attached — a finished
  video with no music applied is a defect.

## Structure — one Quote scene per quote, back-to-back
- **Every scene in this video uses a Quote-category scene design** — this
  format doesn't rotate across scene-design categories the way most recipes
  in this set do; the Quote design, populated correctly, is the whole point.
- **If the template offers multiple visual variants within the Quote
  category, use that variety — don't default to reusing the same one
  variant for every quote.** Before building, check \`${TOOL_NAMES.listSceneDesigns}\` for
  every Quote-category design the template offers. When there's more than
  one, actively rotate across them: never use the same Quote variant on two
  consecutive scenes, and across a video with several quotes, draw on the
  different variants roughly evenly rather than leaning on a single
  favorite. Only repeat a variant back-to-back if the template genuinely
  offers just one Quote design that fits the aspect ratio/content — check
  for that before settling for repetition. Every scene still stays within
  the Quote category (see above); the variety happens within that category.
- **Each Quote scene carries all four elements**: the quote text, the
  speaker's name, their title, and their photo. A Quote scene missing any one
  of these is incomplete — if a chosen design variant doesn't have an asset
  for one of the four, pick a different variant that does rather than
  dropping that element.
- **Quotes are used exactly as gathered — verbatim, never paraphrased,
  shortened, or reworded.** If a quote is too long for a given design's text
  asset, look for another Quote design variant with more room before
  resorting to trimming; if none fits, split that one quote across two
  consecutive Quote scenes rather than cutting words from it.
- **Attribution must be exact and correctly matched**: the right name and the
  right title on the right quote and photo — never mixed up, never
  approximated.
- If more quotes were gathered than the target duration/count from Setup
  allows, use the strongest, most representative subset and tell the user
  how many didn't make it in — never drop quotes silently. Preserve the
  order they came from the source unless the user specifies otherwise.

## Photos — cropped to the face, and genuinely of that person
- **Crop every photo so the focus is on the person's face** — a tight,
  well-centered crop on the face and upper shoulders, not a wide shot where
  the face is small or off to one side (\`${SKILL_NAMES.cropMedia}\` skill, applied here
  specifically for face-focused framing rather than its usual general
  composition guidance).
- **The photo must actually be of the specific person being quoted.** Use the
  photo the user uploaded, or one found alongside that person's quote in the
  crawled source material. If no real photo of that specific person is
  available, ask the user for one (see Setup) rather than substituting a
  generic stock photo of an unrelated person — a stand-in photo would
  misattribute the quote to the wrong face and is not an acceptable fallback
  here, unlike media choices in other recipes.

## Asset timing — no voiceover, so timing is set by reading time
- **Each Quote scene's duration is driven entirely by how long its quote (and
  attribution) takes to comfortably read, not by spoken word timing** — there
  is no voiceover to anchor to. Use \`${TOOL_NAMES.setSceneDuration}\` (auto mode, when
  available, sizes to the actual text length) or set a manual duration long
  enough for a comfortable read; check against \`${TOOL_NAMES.lintVideo}\`'s
  \`scene_reading_time\` to confirm it's neither too short to read nor
  needlessly long. Longer quotes need more time on screen than short ones —
  don't give every scene the same flat duration regardless of quote length.

## Styling
- Colors and styling come from the template or the user's brand kit — apply a
  kit when one exists or the user references their brand (\`${SKILL_NAMES.applyBrandKit}\`
  skill); don't hand-pick color overrides unprompted.

## Finishing
Load \`${SKILL_NAMES.reviewVideo}\` for the standard pass. On top of that, confirm:
- **Every scene is a Quote-category design carrying all four elements** —
  quote, name, title, photo — with none missing or substituted.
- **If the template offers multiple Quote design variants, check that they
  were actually used with variety** (\`${TOOL_NAMES.getVideoState}\`) — no variant
  repeated on consecutive scenes, and no single variant used for every quote
  when others were available. Reusing the same Quote design throughout is a
  defect whenever the template supports more than one.
- **Every quote is verbatim against its source**, with no paraphrasing,
  trimming, or added words, and every name/title pairing is correct and
  matches the right quote and photo.
- **Every photo is cropped with the person's face as the clear focus**, and
  is genuinely a photo of that specific person — not a generic stand-in.
- **Confirm a music track is actually applied** (\`${TOOL_NAMES.getVideoState}\`) and that
  there is no voiceover track anywhere in the video.
- **Check each scene's duration against its quote's actual reading time**
  (\`${TOOL_NAMES.lintVideo}\`'s \`scene_reading_time\`) — longer quotes get more time, short
  ones don't linger needlessly.
- If quotes had to be trimmed down to fit the target duration/count, that was
  a disclosed, deliberate choice — not a silent drop.`,
};
